import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * DriverOnboardingChecklist — Phase 40
 *
 * Mostra una checklist guidata con i 6 step necessari perché il driver sia "pronto":
 *  - Profilo creato
 *  - Veicolo inserito
 *  - Documenti caricati
 *  - Verifica admin
 *  - Remunerazione interna fuori da Stripe (informativa)
 *  - Primo tragitto pendolare attivo
 *
 * - Auto-hide completamente quando `ready === true`.
 * - CTA chiara verso il primo step non completato ("Prossimo passo").
 * - Bar di progresso con percentuale.
 * - Fallback elegante se l'API fallisce (non rompe la Home).
 */
const STEP_META = {
  profile:   { href: "/driver/profile",      cta: "Vedi profilo",         desc: "Hai creato un account come trasportatore DapNow." },
  vehicle:   { href: "/driver/vehicle",      cta: "Aggiungi veicolo",     desc: "Inserisci tipo, modello e targa del veicolo che userai per le consegne." },
  documents: { href: "/driver/documents",    cta: "Carica documenti",     desc: "Carica patente, carta d'identità e libretto di circolazione. La verifica è rapida." },
  verified:  { href: "/driver/documents",    cta: "In attesa di verifica",desc: "Il nostro team controlla i documenti. Di solito entro 24 ore." },
  payout:    { href: "/driver/profile", cta: "Vedi profilo", desc: "La remunerazione dei driver interni è gestita da DapNow fuori da Stripe." },
  route:     { href: "/driver/routes",       cta: "Aggiungi tragitto",    desc: "Inserisci il tuo tragitto pendolare abituale: ti proporremo consegne lungo la stessa strada." },
};

function StepItem({ step, isNext }) {
  const meta = STEP_META[step.id] || {};
  const Icon = step.done ? CheckCircle2 : Circle;
  return (
    <li
      data-testid={`driver-onboarding-step-${step.id}`}
      data-done={step.done}
      className={`rounded-2xl border p-3.5 sm:p-4 flex items-start gap-3.5 transition ${
        step.done
          ? "border-emerald-200 bg-emerald-50/60"
          : isNext
            ? "border-dapnow-blue/40 bg-dapnow-blue-soft/40 ring-2 ring-dapnow-blue/15"
            : "border-dapnow-line bg-white"
      }`}
    >
      <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${step.done ? "text-emerald-600" : isNext ? "text-dapnow-blue" : "text-dapnow-muted"}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`font-semibold tracking-tight text-sm sm:text-base ${step.done ? "text-emerald-900" : "text-dapnow-ink"}`}>
            {step.label}
          </p>
          {isNext && !step.done && (
            <span className="inline-flex items-center gap-1 rounded-full bg-dapnow-blue text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
              Prossimo passo
            </span>
          )}
        </div>
        <p className="text-xs text-dapnow-muted mt-0.5">{meta.desc}</p>
        {!step.done && meta.href && (
          <Link
            to={meta.href}
            data-testid={`driver-onboarding-cta-${step.id}`}
            className={`mt-2 inline-flex items-center gap-1 text-xs font-bold ${isNext ? "text-dapnow-blue" : "text-dapnow-muted hover:text-dapnow-blue"}`}
          >
            {meta.cta} <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>
    </li>
  );
}

export default function DriverOnboardingChecklist() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/driver/onboarding-status");
        if (!cancelled) setStatus(data);
      } catch {
        if (!cancelled) setStatus(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    // Refresh ogni 60s (es. dopo aver completato uno step in un'altra tab)
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (loading) {
    return (
      <div className="card-soft p-6 flex items-center gap-2 text-sm text-dapnow-muted" data-testid="driver-onboarding-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Verifica stato profilo…
      </div>
    );
  }
  if (!status || status.ready) return null;

  // Trova il prossimo step incompleto da evidenziare come "next"
  const nextIdx = status.steps.findIndex((s) => !s.done);

  return (
    <section
      className="card-soft p-5 sm:p-7 overflow-hidden min-w-0 relative"
      data-testid="driver-onboarding-checklist"
    >
      <span className="absolute top-0 left-0 h-full w-1 rounded-l-2xl bg-dapnow-blue" aria-hidden />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-dapnow-blue" />
            <p className="overline text-dapnow-blue">Onboarding driver</p>
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-1">
            Completa il profilo per ricevere ordini
          </h2>
          <p className="text-sm text-dapnow-muted mt-1">
            Pochi passi e sarai pronto a sfruttare i tuoi tragitti pendolari.
          </p>
        </div>
        <div className="text-right">
          <p className="overline text-dapnow-muted text-[10px]">Avanzamento</p>
          <p className="font-display text-2xl font-extrabold tracking-tight">
            <span data-testid="driver-onboarding-progress">{status.progress_pct}%</span>
            <span className="text-sm text-dapnow-muted font-semibold ml-1">
              ({status.completed_count}/{status.total_count})
            </span>
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4 h-2 bg-dapnow-bg rounded-full overflow-hidden" data-testid="driver-onboarding-progressbar">
        <div
          className="h-full bg-gradient-to-r from-dapnow-blue to-emerald-500 transition-all duration-700"
          style={{ width: `${status.progress_pct}%` }}
        />
      </div>

      {/* Steps list */}
      <ul className="mt-5 space-y-2.5" data-testid="driver-onboarding-steps">
        {status.steps.map((s, idx) => (
          <StepItem key={s.id} step={s} isNext={idx === nextIdx} />
        ))}
      </ul>

      {status.next_blocker && (
        <p className="mt-4 text-xs text-dapnow-muted italic" data-testid="driver-onboarding-blocker">
          {status.next_blocker}
        </p>
      )}
    </section>
  );
}
